import type { CadEditor } from './editor/editor';

/**
 * Inline measurement editor for a dimension.
 *
 * Double-clicking a dimension puts a small numeric field over its text: type the size the drawing
 * should be and press `Enter`. The dimension moves its second origin and drags whatever is
 * attached to it, so a dimension both reports *and* drives geometry. `Esc` or a click elsewhere
 * leaves the drawing alone.
 */
export function createDimensionEditor(editor: CadEditor, host: HTMLElement): () => void {
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'dimension-edit';
  input.spellcheck = false;
  input.autocomplete = 'off';
  input.hidden = true;
  host.appendChild(input);

  let open: { id: string; original: string } | null = null;

  const place = (): void => {
    if (!open) return;
    const readout = editor.dimensionReadout(open.id);
    if (!readout) {
      close();
      return;
    }
    const screen = editor.viewport.worldToScreen(readout.position);
    input.style.left = `${screen.x}px`;
    input.style.top = `${screen.y}px`;
  };

  const close = (): void => {
    if (!open) return;
    open = null;
    input.hidden = true;
    input.value = '';
    input.blur();
  };

  const start = (id: string): void => {
    const readout = editor.dimensionReadout(id);
    if (!readout) return;
    const text = readout.value.toFixed(readout.decimals);
    open = { id, original: text };
    input.hidden = false;
    input.value = text;
    place();
    input.focus();
    input.select();
  };

  const commit = (): void => {
    if (!open) return;
    const parsed = Number(input.value.trim().replace(',', '.'));
    const id = open.id;
    close();
    if (!Number.isFinite(parsed) || parsed <= 0) {
      editor.message('DIM  The measurement must be a positive number.');
      return;
    }
    editor.setDimensionValue(id, parsed);
  };

  const onKey = (event: KeyboardEvent): void => {
    if (!open) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      commit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      const original = open.original;
      input.value = original;
      close();
      return;
    }
  };

  input.addEventListener('keydown', onKey);
  // Clicking away keeps the drawing as it was: the field is a prompt, not a live control.
  input.addEventListener('blur', () => close());

  const stopEditing = editor.events.on('dimensionEdit', ({ id }) => start(id));
  const follow = (): void => place();
  const stopView = editor.events.on('view', follow);
  const stopDocument = editor.events.on('document', follow);

  return () => {
    stopEditing();
    stopView();
    stopDocument();
    input.remove();
  };
}
